import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Download,
  Eye,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Image as ImageIcon,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { fileKind, fileTypeLabel, formatFileSize, type FileKind } from "@/utils/files";

const ICONS: Record<FileKind, typeof FileIcon> = {
  image: ImageIcon,
  video: FileVideo,
  pdf: FileText,
  spreadsheet: FileSpreadsheet,
  word: FileText,
  other: FileIcon,
};

const TONES: Record<FileKind, string> = {
  image: "bg-blue-50 text-blue-600",
  video: "bg-sky-50 text-sky-600",
  pdf: "bg-red-50 text-red-600",
  spreadsheet: "bg-green-50 text-green-600",
  word: "bg-indigo-50 text-indigo-600",
  other: "bg-slate-100 text-slate-500",
};

export function AttachmentTile({
  filename,
  contentType,
  sizeBytes,
  note,
  onView,
  onDownload,
  onRemove,
  disabled,
}: {
  filename: string;
  contentType?: string | null;
  sizeBytes?: number;
  note?: ReactNode;
  onView?: () => void;
  onDownload?: () => void | Promise<void>;
  onRemove?: () => void;
  disabled?: boolean;
}) {
  const kind = fileKind(filename, contentType);
  const Icon = ICONS[kind];
  const [downloading, setDownloading] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  async function handleDownload() {
    if (!onDownload || downloading) return;
    setDownloading(true);
    try {
      await onDownload();
    } finally {
      if (alive.current) setDownloading(false);
    }
  }

  const locked = disabled || downloading;

  return (
    <div className="flex items-center gap-2 rounded-md border px-3 py-2">
      <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-md", TONES[kind])}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-800" title={filename}>
          {filename}
        </div>
        <div className="truncate text-xs text-slate-500">
          {fileTypeLabel(filename)}
          {sizeBytes != null && ` · ${formatFileSize(sizeBytes)}`}
        </div>
        {note && <div className="truncate text-xs text-slate-400">{note}</div>}
      </div>
      <div className="flex shrink-0 items-center">
        {onView && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onView}
            disabled={locked}
            title="Preview"
            className="h-8 w-8"
          >
            <Eye className="h-4 w-4" />
            <span className="sr-only">Preview {filename}</span>
          </Button>
        )}
        {onDownload && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleDownload}
            disabled={locked}
            title={downloading ? "Downloading…" : "Download"}
            className="h-8 w-8"
          >
            {downloading ? (
              <Spinner className="h-4 w-4 text-primary" aria-hidden="true" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            <span className="sr-only">
              {downloading ? `Downloading ${filename}` : `Download ${filename}`}
            </span>
          </Button>
        )}
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            disabled={locked}
            title="Remove"
            className="h-8 w-8 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            <span className="sr-only">Remove {filename}</span>
          </Button>
        )}
      </div>
    </div>
  );
}

export function AttachmentGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(16rem,1fr))]">{children}</div>
  );
}
